"use client";

import React from "react";
import { Collapsible } from "../common/collapsible";

export interface TherapistAboutSectionLabels {
  /** Interpolated with `{name}` — e.g. "Hi, I'm {name}!". */
  greeting?: string;
  descriptions?: string;
  rightFit?: string;
  whatToExpect?: string;
  uniqueUnderstanding?: string;
}

export interface TherapistAboutSectionProps {
  firstName: string;
  bio: string;
  uniqueUnderstanding: string[];
  clientDescriptions: string[];
  rightFitFor: string[];
  whatToExpect: string[];
  /** Optional label overrides for user-visible strings. Each field
   *  defaults to English; consumers that need translations pass a
   *  `labels` prop built from their i18n bundle. */
  labels?: TherapistAboutSectionLabels;
}

export function TherapistAboutSection({
  firstName,
  bio,
  uniqueUnderstanding,
  clientDescriptions,
  rightFitFor,
  whatToExpect,
  labels
}: TherapistAboutSectionProps) {
  const greetingLabel = (labels?.greeting ?? "Hi, I'm {name}!").replace(
    '{name}',
    firstName,
  );
  const descriptionsTitle =
    labels?.descriptions ?? 'My clients describe me using words like…';
  const rightFitTitle =
    labels?.rightFit ?? "I'm the right therapist for you if…";
  const whatToExpectTitle =
    labels?.whatToExpect ?? 'If we work together you can expect to feel...';
  const uniqueTitle =
    labels?.uniqueUnderstanding ??
    'My experiences and interests give me a unique understanding of…';
  return (
    <section id="about" className="scroll-mt-8 space-y-8">
      {/* Bio Card */}
      <div className="bg-white rounded-2xl shadow-sm p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          {greetingLabel}
        </h2>
        <p className="text-gray-700 leading-relaxed text-lg">
          {bio}
        </p>
      </div>

      {/* Consolidated About Me Section */}
      <div className="bg-white rounded-2xl shadow-sm p-8">
        <div className="space-y-4">
          <Collapsible
            title={descriptionsTitle}
            defaultOpen={true}
            className="bg-gray-50 border-gray-200"
          >
            <div className="flex flex-wrap gap-2">
              {clientDescriptions.map((word, index) => (
                <span key={index} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                  {word}
                </span>
              ))}
            </div>
          </Collapsible>

          <Collapsible
            title={rightFitTitle}
            defaultOpen={false}
            className="bg-gray-50 border-gray-200"
          >
            <div className="space-y-3">
              {rightFitFor.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </Collapsible>

          <Collapsible
            title={whatToExpectTitle}
            defaultOpen={false}
            className="bg-gray-50 border-gray-200"
          >
            <div className="space-y-3">
              {whatToExpect.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </Collapsible>

          <Collapsible
            title={uniqueTitle}
            defaultOpen={false}
            className="bg-gray-50 border-gray-200"
          >
            <div className="space-y-3">
              {uniqueUnderstanding.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </Collapsible>
        </div>
      </div>
    </section>
  );
}